import { FC, ReactNode } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Box,
  SxProps,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface NoticeDialogProps {
  /**
   * Controls whether the dialog is visible
   */
  show: boolean
  /**
   * Title text to display at the top of the dialog
   */
  title?: string
  /**
   * Message content - can be a string or custom React components
   */
  message?: ReactNode
  /**
   * Children components to render inside the dialog content area
   */
  children?: ReactNode
  /**
   * Show a loading spinner (default: false)
   */
  showSpinner?: boolean
  /**
   * Custom spinner size (default: 40)
   */
  spinnerSize?: number
  /**
   * Maximum width of the dialog (default: 'sm')
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  /**
   * Additional styles to apply to the dialog content
   */
  sx?: SxProps
  /**
   * Additional styles to apply to the dialog paper
   */
  paperSx?: SxProps
  /**
   * Content alignment (default: 'center')
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Action buttons to display at the bottom (optional)
   */
  actions?: ReactNode
}

const NoticeDialog: FC<NoticeDialogProps> = ({
  show,
  title,
  message,
  children,
  showSpinner = false,
  spinnerSize = 40,
  maxWidth = 'sm',
  sx,
  paperSx,
  align = 'center',
  actions,
}) => {
  const theme = useTheme()

  return (
    <Dialog
      open={show}
      maxWidth={maxWidth}
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: 2,
          ...paperSx,
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            textAlign: align,
            fontSize: '1.5rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          justifyContent: 'center',
          gap: 2,
          py: 3,
          ...sx,
        }}
      >
        {showSpinner && (
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress size={spinnerSize} />
          </Box>
        )}
        {message && (
          <Typography
            variant="body1"
            sx={{
              textAlign: align,
              color: theme.palette.text.secondary,
            }}
          >
            {message}
          </Typography>
        )}
        {children}
      </DialogContent>
      {actions && <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>}
    </Dialog>
  )
}

export default NoticeDialog
